// src/components/personal/edit/license/LicenseModal.jsx

import React, { useState, useEffect, useContext } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  IconButton,
  CircularProgress,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import api from '../../../../config/axiosInstance';
import { logApiError } from '../../../../config/apiErrorHandling';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { CalendarContext } from '../../../context/CalendarContext';

const formatCalendarLabel = (calendar) => {
  const position = calendar?.position_name || `Área ${calendar?.position_id ?? '—'}`;
  const start = calendar?.start_date || '—';
  const end = calendar?.end_date || '—';
  return `${position} (${start} a ${end})`;
};

const formatCalendarStatus = (status) => ({
  active: 'Activo',
  inactive: 'Cerrado',
}[status] || status || '—');

const formatLicenseDate = (value) => {
  if (!value) return '—';
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? value : format(parsed, 'dd/MM/yyyy');
};

const getAffectedCalendarsText = (calendars) => {
  const total = Array.isArray(calendars) ? calendars.length : 0;
  return total > 0 ? ` Se actualizaron ${total} calendario(s).` : '';
};

const buildDateRangeReplaceScopes = ({ ci, dateFrom, dateTo, periodStart }) => {
  const scopes = [
    { kind: 'employee_period', ci: String(ci), category: 'rest_between_days' },
  ];

  const start = new Date(`${dateFrom}T00:00:00`);
  const end = new Date(`${dateTo}T00:00:00`);
  for (let current = new Date(start); current <= end; current.setDate(current.getDate() + 1)) {
    const day = current.toISOString().split('T')[0];
    scopes.push({ kind: 'employee_day', ci: String(ci), day });
    scopes.push({ kind: 'day', day });
    if (periodStart && day === periodStart) {
      scopes.push({ kind: 'employee_period', ci: String(ci), category: 'rotation_opening' });
    }
  }

  return scopes;
};

const LicenseModal = ({ open, onClose, ci, station_id, position_id, day, fetchLicenses }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const {
    fetchCalendar,
    calendarData,
    setCalendarData,
    mergeCalendarValidationPatch,
  } = useContext(CalendarContext);

  const [licenseTypes, setLicenseTypes] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [blockedCalendars, setBlockedCalendars] = useState([]);
  const [implicatedCalendars, setImplicatedCalendars] = useState([]);

  const initialDate = day || format(new Date(), 'yyyy-MM-dd');
  const [formValues, setFormValues] = useState({
    date_from: initialDate,
    date_to: initialDate,
    id_type_license: '',
  });

  useEffect(() => {
    if (open) {
      setBlockedCalendars([]);
      setImplicatedCalendars([]);
      setFormValues({
        date_from: day || format(new Date(), 'yyyy-MM-dd'),
        date_to: day || format(new Date(), 'yyyy-MM-dd'),
        id_type_license: '',
      });
      api.get('personal/edit_type_license_list')
         .then(({ data }) => setLicenseTypes(data))
         .catch(() => {
           enqueueSnackbar('No se pudo cargar el listado de inasistencias', { variant: 'error' });
         });
    }
  }, [open, day, enqueueSnackbar]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: value }));
  };

  const buildLicensePayload = ({ confirm = false } = {}) => {
    const payload = {
      ci,
      date_from: formValues.date_from,
      date_to: formValues.date_to,
      id_type_license: formValues.id_type_license,
    };
    if (station_id && position_id) {
      payload.station_id = station_id;
      payload.position_id = position_id;
      payload.day = day;
      payload.calendar_id = calendarData?._id || '';
    }
    if (confirm) {
      payload.confirm = true;
    }
    return payload;
  };

  const getLicenseEndpoint = () =>
    station_id && position_id
      ? 'calendar/add_license'
      : 'personal/edit_license_add';

  const isCalendarFlow = Boolean(station_id && position_id);

  const selectedLicenseType = licenseTypes.find(
    type => String(type.id) === String(formValues.id_type_license)
  );

  const handleSave = async ({ confirm = false } = {}) => {
    if (isSaving) return;
    if (!formValues.date_from || !formValues.date_to || !formValues.id_type_license) {
      enqueueSnackbar('Todos los campos son obligatorios', { variant: 'warning' });
      return;
    }

    if (formValues.date_from > formValues.date_to) {
      enqueueSnackbar('La fecha de inicio no puede ser posterior a la fecha de fin.', { variant: 'warning' });
      return;
    }

    setIsSaving(true);
    try {
      const endpoint = getLicenseEndpoint();
      const params = new URLSearchParams(buildLicensePayload({ confirm })).toString();
      const { data } = await api.post(`${endpoint}?${params}`);

      if (isCalendarFlow) {
        if (data?.updated_calendar) {
          setCalendarData(data.updated_calendar);
          mergeCalendarValidationPatch({
            errors: data.errors,
            warnings: data.warnings,
            replaceScopes: buildDateRangeReplaceScopes({
              ci,
              dateFrom: data?.changed_range?.date_from || formValues.date_from,
              dateTo: data?.changed_range?.date_to || formValues.date_to,
              periodStart: calendarData?.start_date,
            }),
          });
        } else {
          // La licencia ya está guardada: si falta el inicio del calendario cargado,
          // usar el día clickeado o el inicio de la licencia para recargar la vista.
          const inicio = calendarData?.start_date || day || formValues.date_from;
          await fetchCalendar({ date: inicio, station: station_id, position: position_id });
        }
      } else if (typeof fetchLicenses === 'function') {
        await fetchLicenses();
      }

      // Buscar el nombre del tipo de inasistencia seleccionado
      const selectedType = licenseTypes.find(t => t.id === formValues.id_type_license);
      const typeName = selectedType?.name || 'Inasistencia';
      const detailSuffix = getAffectedCalendarsText(data?.affected_calendars);

      enqueueSnackbar(`${typeName} guardada correctamente.${detailSuffix}`, {
        variant: 'success',
        autoHideDuration: 3000,
        action: key => (
          <IconButton onClick={() => closeSnackbar(key)} sx={{ color: 'inherit' }}>
            <CloseIcon />
          </IconButton>
        ),
      });

      onClose();
    } catch (error) {
      const info = logApiError(
        'license.save',
        error,
        'No se pudo guardar la inasistencia. Intenta nuevamente.'
      );
      if (info.status === 409 && info.needsConfirmation) {
        setImplicatedCalendars(info.implicatedCalendars);
        return;
      }
      if (info.status === 409 && info.blockedCalendars.length > 0) {
        setBlockedCalendars(info.blockedCalendars);
      }
      enqueueSnackbar(info.message, { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmImplicatedCalendars = () => {
    setImplicatedCalendars([]);
    handleSave({ confirm: true });
  };

  return (
    <>
      <Modal
        open={open}
        onClose={isSaving ? undefined : onClose}
      >
        <Box
          sx={{
            position: 'relative',
            width: 400,
            mx: 'auto',
            mt: '10%',
          }}
        >
          {isSaving && (
            <LinearProgress
              sx={{ position: 'absolute', top: 0, left: 0, width: '100%' }}
            />
          )}

          <Box
            sx={{
              p: 3,
              backgroundColor: 'white',
              borderRadius: 1,
              boxShadow: 24,
            }}
          >
            <IconButton
              sx={{ position: 'absolute', top: 8, right: 8 }}
              onClick={isSaving ? undefined : onClose}
              disabled={isSaving}
            >
              <CloseIcon />
            </IconButton>

            <Typography variant="h6" align="center" gutterBottom>
              Agregar Inasistencia
            </Typography>


            <TextField
              fullWidth
              margin="normal"
              label="Fecha Inicio"
              name="date_from"
              type="date"
              value={formValues.date_from}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Fecha Fin"
              name="date_to"
              type="date"
              value={formValues.date_to}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              select
              margin="normal"
              label="Tipo de Inasistencia"
              name="id_type_license"
              value={formValues.id_type_license}
              onChange={handleChange}
            >
              {licenseTypes.map(type => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </TextField>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={isSaving}
                startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : null}
              >
                {isSaving ? 'Guardando...' : 'Guardar'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Dialog
        open={blockedCalendars.length > 0}
        onClose={() => setBlockedCalendars([])}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Calendarios cerrados implicados</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            No se pudo guardar la licencia porque hay calendarios cerrados para ese empleado en el rango seleccionado.
          </Alert>
          <Box sx={{ mb: 2, px: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Datos ingresados
            </Typography>
            <Typography variant="body2">
              Tipo de inasistencia: {selectedLicenseType?.name || '—'}
            </Typography>
            <Typography variant="body2">
              Período: {formatLicenseDate(formValues.date_from)} a {formatLicenseDate(formValues.date_to)}
            </Typography>
          </Box>
          <List dense>
            {blockedCalendars.map((calendar, index) => (
              <ListItem key={`${calendar.calendar_id || index}`}>
                <ListItemText
                  primary={formatCalendarLabel(calendar)}
                  secondary={`Estado: ${formatCalendarStatus(calendar?.status)}`}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockedCalendars([])}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={implicatedCalendars.length > 0}
        onClose={isSaving ? undefined : () => setImplicatedCalendars([])}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Calendario activo implicado</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            El empleado ya tiene un calendario activo en el rango seleccionado. Ingresar la
            licencia modificará la planificación actual. ¿Deseas continuar?
          </Alert>
          <List dense>
            {implicatedCalendars.map((calendar, index) => (
              <ListItem key={`${calendar.calendar_id || index}`}>
                <ListItemText
                  primary={formatCalendarLabel(calendar)}
                  secondary={`Estado: ${formatCalendarStatus(calendar?.status)}`}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImplicatedCalendars([])} disabled={isSaving}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmImplicatedCalendars}
            variant="contained"
            color="warning"
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : null}
          >
            Sí, continuar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LicenseModal;
